import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import Dropzone, { DropzoneState, FileRejection } from "react-dropzone";
import { getUrl } from "@aws-amplify/storage";
import { fetchAuthSession } from "@aws-amplify/auth";
import { showToast } from "../../components/toastMessage/toastSlice";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import CustomRadioButton from "../../components/buttons/CustomRadioButton";
import AddModel from "../../components/Add/AddModel";
import AddBottomButton from "../../components/Add/AddBottomButton";
import ErrorMessage from "../../components/ErrorMessages/ErrorMessage";
import RoundedLoader from "../../components/loaders/RoundedLoader";
import Image from "../../components/images/Image";
import MainModel from "../../components/Models/MainModel";
import Button from "../../components/buttons/Button";
import useApi from "../../services/useApi";
import { handleUpload } from "../../components/S3uploads/S3Function";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_RECORDS = 100;

const XLSX_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// Header/data row positions (same for both templates): banner / header / notes / data.
const HEADER_ROW_INDEX = 1;
const FIRST_DATA_ROW_INDEX = 3;

// Sample template + download dropdown option. Files live under
// public/uploads/excels/ in the assets bucket (accessLevel 'guest' prepends
// public/). We sign a short-lived URL via Amplify getUrl rather than linking the
// object directly, because the objects are private (a raw S3 URL -> AccessDenied).
interface SampleTemplateOption {
  id: string;
  label: string;
  key: string;
  disabled?: boolean;
}

// Per-onboarding-type config. Merchant and Customer differ in upload prefix,
// data sheet name, mandatory header columns, and sample template(s). The trigger
// Lambda routes by the upload prefix to the right backend batch handler. The
// admin id is appended at submit time: <uploadPath>/<adminId>/<uuid>/<file>.xlsx.
interface OnboardingTypeConfig {
  uploadPath: string;
  dataSheetName: string;
  mandatoryColumns: string[];
  sampleTemplates: SampleTemplateOption[];
}

const ONBOARDING_CONFIG: Record<string, OnboardingTypeConfig> = {
  merchant: {
    uploadPath: "uploads/bulk-onboarding",
    dataSheetName: "Merchants",
    mandatoryColumns: [
      "User Role (D)",
      "First Name",
      "Date of Birth",
      "Country Code (D)",
      "Phone Number",
      "Gender (D)",
      "Nationality (D)",
      "Profession (D)",
      "Country (D)",
      "Province/State (D)",
      "Region (D)",
      "Wallet Access (D)",
      "Store Name",
      "Business Type (D)",
      "Service Fee Parent User Code",
      "Child Service Fee Percentage",
    ],
    sampleTemplates: [
      {
        id: "upfront",
        label: "Upfront",
        key: "uploads/excels/Merchant_Bulk_Onboarding_Template.xlsx",
      },
      {
        // No Postpaid template exists yet — disabled until the file is available.
        id: "postpaid",
        label: "Postpaid",
        key: "uploads/excels/Merchant_Bulk_Onboarding_Template_Postpaid.xlsx",
        disabled: true,
      },
    ],
  },
  customer: {
    uploadPath: "uploads/bulk-onboarding-customer",
    dataSheetName: "Customers",
    mandatoryColumns: [
      "First Name",
      "Date of Birth",
      "Country Code (D)",
      "Phone Number",
      "Gender (D)",
      "Nationality (D)",
      "Profession (D)",
      "Country (D)",
      "Province/State (D)",
      "Region (D)",
      "Wallet Access (D)",
    ],
    sampleTemplates: [
      {
        id: "individual",
        label: "Individual Customer",
        key: "uploads/excels/Customer_Bulk_Onboarding_Template.xlsx",
      },
      {
        // No Corporate template exists yet — disabled until the file is available.
        id: "corporate",
        label: "Corporate Customer",
        key: "uploads/excels/Corporate_Bulk_Onboarding_Template.xlsx",
        disabled: true,
      },
    ],
  },
};

// Cheap, synchronous checks (format + size) that don't need to read the file.
const validateXlsx = (file: File): string | null => {
  const isXlsxName = /\.xlsx$/i.test(file.name);
  if (!XLSX_MIME_TYPES.includes(file.type) && !isXlsxName) {
    return "Invalid file format";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "Upload Failed: File exceeds 10MB.";
  }
  return null;
};

const isCellEmpty = (cell: unknown): boolean =>
  cell === null || cell === undefined || String(cell).trim() === "";

// Parses the .xlsx and validates its contents. Returns an error message string
// to display inline, or null when the file passes. Error strings match the
// messages in the design.
const validateXlsxContent = async (
  file: File,
  config: OnboardingTypeConfig
): Promise<string | null> => {
  // Dynamic import keeps the parser out of the initial bundle. The package has
  // no root export — the browser build is exposed under /browser.
  const readXlsxFile = (await import("read-excel-file/browser")).default;

  type Cell = string | number | boolean | Date | null;

  let parsed: unknown;
  try {
    parsed = await readXlsxFile(file);
  } catch {
    // Unreadable / corrupt / not a real spreadsheet.
    return "Invalid file format";
  }

  // For multi-sheet workbooks the parser returns [{ sheet, data }, ...]; for a
  // single sheet it returns a flat Cell[][]. Normalise to the Merchants sheet's
  // rows either way.
  let rows: Cell[][];
  if (
    Array.isArray(parsed) &&
    parsed.length > 0 &&
    parsed[0] !== null &&
    typeof parsed[0] === "object" &&
    "data" in (parsed[0] as object)
  ) {
    const sheets = parsed as Array<{ sheet: string; data: Cell[][] }>;
    const merchants =
      sheets.find((s) => s.sheet === config.dataSheetName) || sheets[0];
    rows = merchants.data;
  } else {
    rows = (parsed as Cell[][]) || [];
  }

  // Must have at least the header row plus one data row.
  if (rows.length <= HEADER_ROW_INDEX) {
    return "Invalid file format";
  }

  // Header row must match the mandatory columns exactly and in order.
  const header = rows[HEADER_ROW_INDEX].map((c) =>
    c === null || c === undefined ? "" : String(c).trim()
  );
  const headerMatches =
    header.length === config.mandatoryColumns.length &&
    config.mandatoryColumns.every((col, i) => header[i] === col);
  if (!headerMatches) {
    return "Invalid number of columns or rows";
  }

  // Data rows start after the header + instruction rows. Drop fully-blank rows.
  const dataRows = rows
    .slice(FIRST_DATA_ROW_INDEX)
    .filter((row) => row.some((cell) => !isCellEmpty(cell)));

  if (dataRows.length === 0) {
    return "File has no records";
  }

  if (dataRows.length > MAX_RECORDS) {
    return `Upload failed: File exceeds the ${MAX_RECORDS} record limit`;
  }

  // Every mandatory cell in every data row must be filled.
  const hasMissingData = dataRows.some((row) =>
    config.mandatoryColumns.some((_, col) => isCellEmpty(row[col]))
  );
  if (hasMissingData) {
    return "File has missing data";
  }

  return null;
};

function BulkOnboarding() {
  const dispatch = useDispatch();
  const { getAPI } = useApi();

  // Only "merchant" is active for now; "customer" is shown but disabled.
  const [selectedOption, setSelectedOption] = useState("merchant");
  // Selected type drives the upload prefix, expected sheet/columns, and template.
  const config = ONBOARDING_CONFIG[selectedOption] ?? ONBOARDING_CONFIG.merchant;
  // The file is held locally after selection and only uploaded to S3 on Save —
  // uploading is what triggers the backend Lambda/Batch, so it must wait for the
  // user's explicit Save click.
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  // True while the chosen .xlsx is being parsed and validated.
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Shown after a successful upload to confirm the file is under review.
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // Delete/replace confirmation for the selected (not-yet-uploaded) file.
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Download-sample dropdown (Upfront / Postpaid)
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const downloadRef = useRef<HTMLDivElement>(null);

  // Close the dropdown when clicking outside it.
  useEffect(() => {
    if (!downloadOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target as Node)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [downloadOpen]);

  const resetUpload = () => {
    setSelectedFile(null);
    setError(null);
  };

  const handleOptionChange = (value: string) => {
    setSelectedOption(value);
    resetUpload();
  };

  // Validate the file and hold it locally. Does NOT upload — that happens on Save.
  // Runs the cheap format/size check first, then parses the .xlsx to validate its
  // columns and records, surfacing any error inline.
  const selectFile = async (file: File) => {
    setError(null);
    const formatError = validateXlsx(file);
    if (formatError) {
      setSelectedFile(null);
      setError(formatError);
      return;
    }
    setValidating(true);
    try {
      const contentError = await validateXlsxContent(file, config);
      if (contentError) {
        setSelectedFile(null);
        setError(contentError);
        return;
      }
      setSelectedFile(file);
    } catch (err) {
      // Parser failed to load or threw unexpectedly — don't leave the user with
      // no feedback.
      console.error("Failed to validate .xlsx file:", err);
      setSelectedFile(null);
      setError("Invalid file format");
    } finally {
      setValidating(false);
    }
  };

  const handleDrop = (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[]
  ) => {
    setError(null);
    if (rejectedFiles.length > 0) {
      setError(validateXlsx(rejectedFiles[0].file as File) || "File upload failed.");
      return;
    }
    if (acceptedFiles.length > 0) {
      selectFile(acceptedFiles[0]);
    }
  };

  const handleManualUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      selectFile(event.target.files[0]);
    }
  };

  // Download the currently-selected (in-memory) file back to the user.
  const handleDownloadSelected = () => {
    if (!selectedFile) return;
    const url = URL.createObjectURL(selectedFile);
    const a = document.createElement("a");
    a.href = url;
    a.download = selectedFile.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Delete confirmed in the modal — clear the selected file.
  const handleConfirmDelete = () => {
    setShowDeleteModal(false);
    resetUpload();
  };

  // Replace chosen in the modal — close it and open the file picker.
  const handleReplaceFile = () => {
    setShowDeleteModal(false);
    handleClickUpload();
  };

  const handleClickUpload = () => {
    setError(null);
    const input = document.getElementById("bulk-onboarding-upload") as HTMLInputElement;
    if (input) {
      input.value = "";
      input.click();
    }
  };

  const handleDownloadSample = async (option: {
    id: string;
    label: string;
    key: string;
  }) => {
    setDownloadOpen(false);
    setDownloadingId(option.id);
    try {
      // Sign a short-lived GET URL for the private template object.
      const { url } = await getUrl({
        key: option.key,
        options: { accessLevel: "guest" },
      });
      // Fetch the file and download the blob via a temporary anchor. We avoid
      // window.open(url, "_blank") because opening the cross-origin signed URL
      // flashes a blank tab before the browser resolves it to a download.
      const response = await fetch(url.toString());
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = option.key.split("/").pop() || `${option.label}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      dispatch(
        showToast({
          type: "success",
          message: ".xlsx file template downloaded successfully",
        })
      );
    } catch (err) {
      console.error("Failed to get sample template URL:", err);
      dispatch(
        showToast({
          type: "error",
          message: `${option.label} sample template is not available yet.`,
        })
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError("This field is required");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Resolve the logged-in admin's id so the backend can attribute the
      // onboarding (onboarded_by). Best-effort: if it can't be resolved we still
      // upload to the base path and the batch records a null onboarder.
      const adminId = await resolveAdminId();
      const uploadPath = adminId
        ? `${config.uploadPath}/${adminId}`
        : config.uploadPath;

      // Upload now — landing the file under public/uploads/bulk-onboarding/ is
      // what fires the backend S3 -> Lambda -> Batch onboarding pipeline.
      const key = await handleUpload(selectedFile, uploadPath);
      if (!key) {
        setError("Upload failed.");
        return;
      }
      resetUpload();
      setShowSuccessModal(true);
    } catch {
      setError("Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  // Resolve the current admin's DB id (admins.id) from the session email, used
  // to attribute the bulk onboarding. Returns null on any failure.
  const resolveAdminId = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      const email = session?.tokens?.idToken?.payload?.email as string | undefined;
      if (!email) return null;
      const { error: apiError, data } = await getAPI(
        `admins/email/${encodeURIComponent(email)}`
      );
      if (apiError) return null;
      return (data?.id as string) || (data?.data?.id as string) || null;
    } catch {
      return null;
    }
  };

  return (
    <div>
      <Breadcrumbs
        navPath={[
          "Bulk Onboarding",
          selectedOption === "customer" ? "Customer" : "Merchant",
        ]}
        heading="Bulk Onboarding"
        noNavigation
        links={["/bulk-onboarding"]}
        hasAddPrivilege={true}
        list={true}
      />

      <AddModel>
        {/* Header: Merchant/Customer radios + Download Sample button */}
        <div className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-outline">
          <div className="flex-1 min-w-0">
            <CustomRadioButton
              options={[
                { id: "merchant", label: "Merchant" },
                { id: "customer", label: "Customer" },
              ]}
              selected={selectedOption}
              padding="p-0"
              onChange={handleOptionChange}
            />
          </div>
          <div className="shrink-0 relative" ref={downloadRef}>
            <button
              type="button"
              data-testid="download-sample"
              onClick={() => setDownloadOpen((open) => !open)}
              className="h-[44px] gap-2 px-4 flex items-center justify-center rounded-lg border border-primary_text text-p_primary text-large transition"
            >
              <Image src="download" />
              <span className="hidden xl:inline font-normal">Download Sample .xlsx File</span>
              <span
                className={`transition-transform duration-200 ${downloadOpen ? "rotate-180" : ""}`}
              >
                <Image src="dropdown-down-arrow" />
              </span>
            </button>
            {downloadOpen && (
              <div className="absolute right-0 mt-2 w-[227px] h-[112px] bg-white rounded-xl border border-P_light_active shadow-lg z-[1000] px-1.5 py-3">
                {config.sampleTemplates.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    data-testid={`download-sample-${option.id}`}
                    disabled={downloadingId !== null || option.disabled}
                    onClick={() => handleDownloadSample(option)}
                    className="w-full text-left px-4 py-2.5 rounded-lg text-body1 font-semibold text-p_primary hover:bg-p_light transition disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center gap-2"
                  >
                    {downloadingId === option.id && <RoundedLoader />}
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upload zone + instructions */}
        <div className="flex flex-col xl:flex-row">
          <div className="w-full xl:min-w-[25.625rem] xl:w-auto pt-8">
            {!selectedFile ? (
              <Dropzone
                onDrop={handleDrop}
                disabled={loading || validating}
                accept={{
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                    [".xlsx"],
                }}
                maxSize={MAX_FILE_SIZE}
                multiple={false}
              >
                {({ getRootProps, isDragActive }: DropzoneState) => (
                  <div
                    {...getRootProps()}
                    className={`w-full max-w-md pr-6 sm:pr-12 pl-6 sm:pl-12 pb-6 sm:pb-9 pt-6 sm:pt-9 border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-center transition ${
                      isDragActive
                        ? "border-P_light_active bg-blue-50"
                        : error
                        ? "border-negative bg-white"
                        : "border-p_light_hover bg-white"
                    }`}
                  >
                    <input
                      type="file"
                      id="bulk-onboarding-upload"
                      data-testid="input-bulk-onboarding-upload"
                      disabled={loading || validating}
                      className="hidden"
                      accept=".xlsx"
                      title="Upload file input"
                      aria-label="Upload file input"
                      onChange={handleManualUpload}
                    />
                    <button
                      className="h-[44px] gap-2 w-[110px] flex items-center justify-center rounded-lg border border-p_primary text-p_primary text-body1 transition disabled:opacity-60"
                      onClick={handleClickUpload}
                      disabled={loading || validating}
                      aria-label="Upload Button"
                      data-testid="bulk-onboarding-upload"
                    >
                      {validating ? (
                        <RoundedLoader />
                      ) : (
                        <>
                          <div className="-rotate-90">
                            <Image src="log-out" />
                          </div>
                          Upload
                        </>
                      )}
                    </button>
                    <p className="text-secondary_text text-medium mt-1.5">Or</p>
                    <p className="text-body1 text-primary_text mb-4">Drag &amp; Drop</p>
                    <p className="text-secondary_text text-body1">
                      Max file size: 10MB
                      <br />
                      Supported file type: .xlsx format
                    </p>
                  </div>
                )}
              </Dropzone>
            ) : (
              <div className="w-80 h-[52px] flex items-center justify-between bg-p_light border border-outline rounded-lg px-[18px] py-[14px]">
                <h1
                  className="text-body1 text-primary_text truncate pr-4 flex-1 min-w-0"
                  data-testid="bulk-onboarding-upload"
                  title={selectedFile.name}
                >
                  {selectedFile.name}
                </h1>
                <div className="flex gap-4 shrink-0">
                  <Image
                    src="delete_red"
                    onClick={() => setShowDeleteModal(true)}
                    className="cursor-pointer"
                    testId="delete_bulk-onboarding-upload"
                  />
                  <Image
                    src="download"
                    onClick={handleDownloadSelected}
                    className="cursor-pointer"
                    testId="download_bulk-onboarding-upload"
                  />
                </div>
                {/* Hidden input used by the "Replace the file" action. */}
                <input
                  type="file"
                  id="bulk-onboarding-upload"
                  data-testid="input-bulk-onboarding-upload"
                  className="hidden"
                  accept=".xlsx"
                  title="Replace file input"
                  aria-label="Replace file input"
                  onChange={handleManualUpload}
                />
              </div>
            )}
            {error && (
              <ErrorMessage
                errorMessage={error}
                className="text-negative"
                testId="error-bulk-onboarding-upload"
              />
            )}
          </div>

          {/* Instructions */}
          <div className="xl:ml-[50px]">
            <h3 className="text-large font-semibold mb-1 mt-7 text-primary_text">
              Upload File
            </h3>
            <div className="text-body1 text-primary_text">
              {[
                "Maximum 100 records allowed.",
                "Only .xlsx files are accepted.",
                "The file must include all mandatory columns in order — no extra or missing columns.",
                // ZoodPay service-fee-parent note applies to merchants only.
                ...(selectedOption === "customer"
                  ? []
                  : [
                      'If the parent is ZoodPay, enter "ZoodPay" in the Service Fee Parent User Code column.',
                    ]),
              ].map((line) => (
                <div className="flex items-start" key={line}>
                  <span className="text-xs font-bold mx-2 mt-0.5">•</span>
                  <p className="text-primary_text font-medium">{line}</p>
                </div>
              ))}
              <div className="flex items-start">
                <span className="text-xs font-bold mx-2 mt-0.5">•</span>
                <div>
                  <span className="text-primary_text font-medium">Example: </span>
                  <span className="text-primary_text">
                    {config.mandatoryColumns
                      .map((c) => c.replace(/\s*\(D\)\s*$/, ""))
                      .join(", ")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AddModel>

      <div>
        <AddBottomButton
          buttonName="Save"
          buttonWidth="w-[174px]"
          align="end"
          onClick={handleSubmit}
          loading={loading}
          testID="submit"
          disabled={loading || !selectedFile}
        />
      </div>

      {showSuccessModal && (
        <MainModel
          modelWidth="w-full max-w-[620px]"
          handleClose={() => setShowSuccessModal(false)}
          disableOutsideClick
        >
          <div data-testid="bulk-onboarding-success-modal">
            <div className="flex justify-center mb-4">
              <img src="/images/alert-circle.svg" alt="Under review" className="w-12 h-12" />
            </div>
            <h2 className="text-heading3 font-semibold text-primary_text text-center pb-4 border-b border-outline">
              Bulk Onboarding Under Review
            </h2>
            <p className="text-body1 text-secondary_text mt-4">
              Your file has been submitted successfully. Here's what happens next:
            </p>
            <ul className="mt-2 text-body1 text-secondary_text">
              {[
                `You'll receive an email report with the ${selectedOption} onboarding summary.`,
                "The report will highlight any data errors found during review.",
                `Approved ${selectedOption}s will appear in the Onboarding / Modification Request section.`,
              ].map((line) => (
                <li className="flex items-start mb-2" key={line}>
                  <span className="mx-2 mt-0.5">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-end mt-6">
              <Button
                buttonName="Go back"
                buttonWidth="w-[120px]"
                buttonHight="h-[45px]"
                textSize="text-small"
                buttonOnclick={() => setShowSuccessModal(false)}
                testId="success-go-back"
              />
            </div>
          </div>
        </MainModel>
      )}

      {showDeleteModal && (
        <MainModel
          modelWidth="w-full max-w-[480px]"
          handleClose={() => setShowDeleteModal(false)}
          isClose
        >
          <div data-testid="bulk-onboarding-delete-modal">
            <div className="flex justify-center mb-4">
              <img src="/images/alert-circle.svg" alt="Confirm delete" className="w-12 h-12" />
            </div>
            <h2 className="text-heading3 font-semibold text-primary_text text-center pb-4 border-b border-outline">
              Do you want to Delete ?
            </h2>
            <p className="text-body1 text-secondary_text text-center mt-4">
              Do you want to delete .xlsx file or you want to replace the file ?
            </p>
            <div className="flex justify-center gap-4 mt-6">
              <Button
                buttonName="Replace the file"
                buttonWidth="w-[160px]"
                buttonHight="h-[45px]"
                textSize="text-small"
                buttonOnclick={handleReplaceFile}
                testId="delete-modal-replace"
              />
              <Button
                buttonName="Delete"
                buttonWidth="w-[160px]"
                buttonHight="h-[45px]"
                textSize="text-small"
                bgColor="bg-negative"
                buttonOnclick={handleConfirmDelete}
                testId="delete-modal-confirm"
              />
            </div>
          </div>
        </MainModel>
      )}
    </div>
  );
}

export default BulkOnboarding;
